import React, { useState, useId } from "react";
import styles from "./Toggle.module.css";

interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  label?: string;
  disabled?: boolean;
  id?: string;
  /**
   * Een eigen naam voor hulpsoftware, voor het geval het zichtbare label
   * ontbreekt of niet onderscheidt (issue 257). In een lijst met een schakelaar
   * per rij is "Actief" bij elke rij hetzelfde woord: op het scherm zie je bij
   * welke rij hij hoort, met een schermlezer niet. Zet hier dan de rij bij —
   * "Goedkeuringsflow Verlofaanvraag actief".
   */
  ariaLabel?: string;
}

const Toggle: React.FC<ToggleProps> = ({
  checked,
  onChange,
  label,
  disabled = false,
  id,
  ariaLabel,
}) => {
  const generatedId = useId();
  const toggleId = id ?? generatedId;
  const [internalChecked, setInternalChecked] = useState(false);

  const isChecked = checked ?? internalChecked;

  const handleClick = () => {
    if (disabled) return;
    const next = !isChecked;
    if (onChange) {
      onChange(next);
    } else {
      setInternalChecked(next);
    }
  };

  const trackClass = [
    styles.track,
    isChecked ? styles.checked : "",
    disabled ? styles.disabled : "",
  ]
    .filter(Boolean)
    .join(" ");

  const thumbClass = [
    styles.thumb,
    isChecked ? styles.thumbChecked : "",
  ]
    .filter(Boolean)
    .join(" ");

  // DE NAAM VAN DEZE SCHAKELAAR (issue 257, WCAG 2.2 AA 4.1.2).
  //
  // Hier stond `aria-label={label && !label ? undefined : undefined}` — een
  // tautologie: de conditie is altijd onwaar en beide takken geven undefined, dus
  // het attribuut werd nooit gezet. En de `<label htmlFor>` hieronder wijst naar
  // een knop waarin alleen een `<span>` zit, dus die levert ook geen naam op.
  // Resultaat: een `role="switch"` zonder naam — een schermlezer kondigde
  // "switch" aan en verder niets.
  //
  // Nu: een eigen `ariaLabel` wint, anders wijst de knop naar het zichtbare label.
  const labelId = `${toggleId}-label`;

  return (
    <div className={styles.wrapper}>
      <button
        id={toggleId}
        type="button"
        role="switch"
        aria-checked={isChecked}
        aria-label={ariaLabel}
        aria-labelledby={!ariaLabel && label ? labelId : undefined}
        className={trackClass}
        onClick={handleClick}
        disabled={disabled}
      >
        <span className={thumbClass} />
      </button>
      {label && (
        <label id={labelId} htmlFor={toggleId} className={styles.label}>
          {label}
        </label>
      )}
    </div>
  );
};

export { Toggle };
export type { ToggleProps };
export default Toggle;
